<template>
  <div
    class="flex justify-center items-center w-full h-full"
    id="layout-wrapper"
    ref="layoutWrapperRef"
  >
    <div id="layout-wrapper-ratio" class="relative" :style="aspectRatioCss">
      <div
        class="absolute top-0 left-0 grid items-center flex-wrap gap-2 w-full h-full grid-flow-col"
        :class="gridLayoutClasses"
      >
        <UserLeftOverlay v-if="!clinicianStreams.length" />

        <template
          :key="`n-${clinicianStream.jitsiUserId}`"
          v-for="(clinicianStream, index) in clinicianStreams"
        >
          <div
            :class="getGridItemClasses(clinicianStream)"
            :style="getGridItemStyle(clinicianStream, index)"
            v-show="!clinicianStream?.screenshareOff"
          >
            <div class="h-0 w-full relative" style="padding-top: 56.25%">
              <div
                class="
                  absolute
                  w-full
                  h-full
                  top-0
                  left-0
                  items-center
                  justify-center
                  flex flex-col
                "
              >
                <StreamPlayerRemote
                  :jitsiUserId="clinicianStream.jitsiUserId"
                  :showAudio="!isHiddenConsultationStream && !clinicianStream.streamMuted"
                  :showVideo="
                    !isHiddenConsultationStream &&
                    !isRemoteVideoOff(clinicianStream.jitsiUserId) &&
                    !clinicianStream.streamMuted
                  "
                  :isAudioMuted="
                    sessionStore.isPutOnHold ||
                    sessionStore.isPhasePaused ||
                    clinicianStream.streamMuted
                  "
                  :setVideoAudioRefs="setVideoAudioRefs"
                />
                <div
                  class="justify-center items-center h-full w-full rounded-2xl bg-vice-medium-teal"
                  :class="{
                    flex:
                      clinicianStream.streamMuted || !(clinicianStream.userId === 'Stethoscope'),
                  }"
                  v-show="
                    isHiddenConsultationStream ||
                    isRemoteVideoOff(clinicianStream.jitsiUserId) ||
                    clinicianStream.streamMuted
                  "
                >
                  <!-- Stethoscope -->
                  <div v-if="clinicianStream.name === DISPLAY_NAME.STETHOSCOPE" class="text-white">
                    <StethoscopeAudioVisualizer
                      v-if="
                        clinicianStream.userId === 'Stethoscope' &&
                        !!clinicianStream?.tracks[0]?.stream &&
                        !clinicianStream.streamMuted
                      "
                      :stream="clinicianStream.tracks[0].stream"
                    />
                    <ViceStethoscope
                      v-if="clinicianStream.userId === 'Stethoscope'"
                      :class="iconSizeCSS(clinicianStream.jitsiUserId)"
                    />
                  </div>

                  <!-- Other streams without video -->
                  <div v-else-if="isMedicalDevice(clinicianStream.jitsiUserId)">
                    <div class="flex justify-center">
                      <ViceMonitor
                        v-if="clinicianStream.userId === 'Monitor'"
                        :class="iconSizeCSS(clinicianStream.jitsiUserId)"
                      />
                      <ViceOtoscope
                        v-if="clinicianStream.userId === 'Otoscope'"
                        :class="iconSizeCSS(clinicianStream.jitsiUserId)"
                      />
                      <ViceCamera
                        v-if="clinicianStream.userId === 'External Camera'"
                        :class="iconSizeCSS(clinicianStream.jitsiUserId)"
                      />
                      <ViceDermatoscope
                        v-if="clinicianStream.userId === 'Dermatoscope'"
                        :class="iconSizeCSS(clinicianStream.jitsiUserId)"
                      />
                    </div>
                  </div>
                  <!-- Clinician main stream without video -->
                  <VideoOff
                    v-else
                    :class="{
                      'sm:h-16 sm:w-16 sm:text-xl': shouldBeMinimized(clinicianStream),
                    }"
                    :name="remoteUserProps[clinicianStream.jitsiUserId]['name']"
                    :profile_photo_url="remoteUserProps[clinicianStream.jitsiUserId]['avatar']"
                  />
                </div>

                <UserLabel
                  :displayName="getLabelName(clinicianStream.jitsiUserId)"
                  :isExternalDevice="true"
                />

                <!--  Layout toggle options  -->
                <div class="absolute top-0 right-0 z-10">
                  <GridItemActionsPanel
                    :small="shouldBeMinimized(clinicianStream)"
                    v-if="clinicianStreams.length > 1"
                    :isMuted="clinicianStream.streamMuted"
                    :streamId="clinicianStream.jitsiUserId"
                    :isFocused="streamsLayoutStore.focusedStreamId == clinicianStream.jitsiUserId"
                    @focusToggled="toggleFocus"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <SessionPausedNotification
      class="z-20"
      v-if="sessionStore.isPhasePaused && !sessionStore.isPutOnHold"
    />
    <SessionOnHoldOverlay v-if="sessionStore.isPutOnHold" />
  </div>
  <!-- TODO: Other participants audio tracks -->
  <template
    :key="`n-${participantsStream.jitsiUserId}`"
    v-for="participantsStream in participantsStreams"
  >
    <StreamPlayerRemote
      :jitsiUserId="participantsStream.jitsiUserId"
      :showAudio="!isHiddenConsultationStream"
      :isAudioMuted="sessionStore.isPutOnHold"
      :showVideo="false"
      :setVideoAudioRefs="setVideoAudioRefs"
    />
  </template>

  <!-- Local camera preview -->
  <div
    class="group block overflow-hidden z-20 absolute bottom-0 right-2 w-56"
    v-if="!sessionStore.userIsObserver"
  >
    <StreamPlayerLocal
      class="rounded-xl camera"
      :showTitle="true"
      :showControls="false"
      :isMinimizable="true"
    />
  </div>
</template>

<script>
import { ref, reactive, onUnmounted, onMounted, watch, computed } from 'vue';
import {
  COMMANDS,
  DISPLAY_NAME,
  STETHOSCOPE_EVENTS,
  USER_ROLE,
  CAMERA_TYPES,
} from '@/Utils/vice-constants';
import StreamPlayerLocal from '@/Components/Session/Grid/StreamPlayerLocal.vue';
import StreamPlayerRemote from '@/Components/Session/Grid/StreamPlayerRemote.vue';
import VideoOff from '@/Components/Session/Grid/VideoOff.vue';
import { SocketioClient } from '@/Services/SocketioClient';
import useTilesLayoutStore from '@/Stores/useTilesLayoutStore';
import GridItemActionsPanel from '@/Components/Session/Grid/GridItemActionsPanel.vue';
import useSessionStore from '@/Stores/useSessionStore';
import useIOTDevicesStore from '@/Stores/useIOTDevicesStore';
import SessionPausedNotification from '@/Components/Session/Overlays/SessionPausedOverlay';
import SessionOnHoldOverlay from '@/Components/Session/Overlays/SessionOnHoldOverlay';
import UserLeftOverlay from '@/Components/Session/Overlays/UserLeftOverlay';
import useConsultationHandlers from '@/Composables/useConsultationHandlers';
import useLayoutComposable from '@/Composables/useLayoutComposable';
import useJitsiTracksComposable from '@/Composables/useJitsiTracksComposable';
import useClinicianStreamsComposable from '@/Composables/useClinicianStreamsComposable';
import JitsiService from '@/Services/JitsiService';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import ViceStethoscope from '@/Components/Icons/ViceStethoscope.vue';
import StethoscopeAudioVisualizer from '@/Components/Session/Grid/StethoscopeAudioVisualizer.vue';
import UserLabel from '@/Components/Session/Grid/UserLabel.vue';
import ViceDermatoscope from '@/Components/Icons/ViceDermatoscope.vue';
import ViceMonitor from '@/Components/Icons/ViceMonitor.vue';
import ViceCamera from '@/Components/Icons/ViceCamera.vue';
import ViceOtoscope from '@/Components/Icons/ViceOtoscope.vue';

export default {
  components: {
    VideoOff,
    UserLabel,
    ViceCamera,
    ViceMonitor,
    ViceOtoscope,
    ViceStethoscope,
    UserLeftOverlay,
    ViceDermatoscope,
    StreamPlayerLocal,
    StreamPlayerRemote,
    SessionOnHoldOverlay,
    GridItemActionsPanel,
    SessionPausedNotification,
    StethoscopeAudioVisualizer,
  },
  setup(props) {
    const sessionStore = useSessionStore();
    const streamsLayoutStore = useTilesLayoutStore();
    const streamingDeviceStore = useIOTDevicesStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const videoAudioRefs = reactive({});
    const remoteUserProps = reactive({});

    const myJitsiId = ref(null);
    const jitsiRoom = ref(null);
    const disconnect = ref(null);
    const pulseVisualizer = ref(null);
    const localConnectionQuality = ref(0);
    const isStethoscopeConnected = ref(false);

    const internalCameraUser = computed(
      () => multiCameraDevicesStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM],
    );

    const isReady = computed(
      () => jitsiRoom.value && (checkIfAudioAndVideoTracksExist() || sessionStore.userIsObserver),
    );

    const clinicianStreams = computed(() => {
      const filteredStreams = _.filter(
        remoteUserProps,
        (_props) => useStreams.isClinicianName(_props.name) || _props.name == DISPLAY_NAME.VUZIX,
      );
      if (isStethoscopeConnected?.value) {
        filteredStreams.push({
          name: DISPLAY_NAME.STETHOSCOPE,
          role: USER_ROLE.PARTICIPANT,
          userId: 'stethoscopeId',
          jitsiUserId: 'stethoscopeId',
          tracks: [],
          audio: true,
          video: false,
          videoType: '',
        });
      }
      return filteredStreams;
    });

    /**
     * When a user joins a conference, add the media track to the conference/room
     */
    const onConferenceJoined = async () => {
      useActions.isJoined.value = true;
      const localTracks = internalCameraUser.value?.localTracks;
      const conferenceAudioTrack = internalCameraUser.value.jitsiRoom.getLocalAudioTrack();
      if (localTracks?.length) {
        for (const newTrack of localTracks) {
          // Replace audio track if already exists
          if (conferenceAudioTrack?.type === newTrack?.type || conferenceAudioTrack === newTrack) {
            // eslint-disable-next-line no-await-in-loop
            await internalCameraUser.value.jitsiRoom?.replaceTrack(conferenceAudioTrack, newTrack);
          } else {
            // eslint-disable-next-line no-await-in-loop
            await internalCameraUser.value.jitsiRoom?.addTrack(newTrack);
          }
        }
      }
    };

    const useActions = useJitsiTracksComposable(props, {
      myJitsiId,
      jitsiRoom,
      videoAudioRefs,
      internalCameraUser,
    });

    const { events, commands } = useConsultationHandlers({
      remoteUserProps,
      videoAudioRefs,
      onConferenceJoined,
      myJitsiId,
      resetMicState: useActions.resetMicState,
      room: jitsiRoom,
      localConnectionQuality,
    });

    const useStyles = useLayoutComposable({
      clinicianStreams,
      streamsLayoutStore,
    });

    const setVideoAudioRefs = useActions.setVideoAudioRefs;

    const useStreams = useClinicianStreamsComposable({ remoteUserProps });

    const checkIfAudioAndVideoTracksExist = () => {
      const trackTypes = [];
      multiCameraDevicesStore.checkPermissions().then((permitted) => {
        if (!permitted.camera || !permitted.microphone) {
          console.log('Not enough permissions. Check them now...');
          sessionStore.checkForPermissions();
        }
      });

      internalCameraUser.value?.localTracks?.forEach((track) => {
        trackTypes.push(track.type);
        if (track.stream?.active === false && sessionStore.isMyCameraOn) {
          track.unmute();
        }
      });

      return trackTypes.includes('audio') && trackTypes.includes('video');
    };

    watch(isReady, (isReadyToJoin) => {
      if (isReadyToJoin) {
        useActions.initializeRoom();
      }
    });

    watch(
      () => sessionStore.isPutOnHold,
      (isOnHold) => {
        jitsiRoom.value?.sendCommandOnce(COMMANDS.PUT_ON_HOLD, {
          value: COMMANDS.PUT_ON_HOLD,
          attributes: {
            isOnHold,
            userRoomId: myJitsiId.value,
          },
          children: [],
        });
      },
    );

    onMounted(async () => {
      const { room, disconnect: disconnectJitsi } = await JitsiService().makeConnection(
        sessionStore.sessionId,
        events,
        commands,
      );
      jitsiRoom.value = room;
      disconnect.value = disconnectJitsi;
      multiCameraDevicesStore.setActiveJitsiRoom(CAMERA_TYPES.INTERNAL_WEBCAM, room);

      // Todo: init only when stetoscope is connected
      await initializeStethoscopeSocketIO();
    });

    onUnmounted(async () => {
      try {
        await disconnect.value();
      } catch (error) {
        console.error(error);
      }
    });

    const initializeStethoscopeSocketIO = async () => {
      streamingDeviceStore.iotSockets.stethoscope = await SocketioClient(sessionStore.sessionId);

      streamingDeviceStore.iotSockets.stethoscope.on(STETHOSCOPE_EVENTS.SIGNAL, ({ signal }) => {
        isStethoscopeConnected.value = true;
        if (!pulseVisualizer.value) return;
        pulseVisualizer.value[0]?.feedPcmPlayer(signal);
      });

      streamingDeviceStore.iotSockets.stethoscope.on(STETHOSCOPE_EVENTS.DEVICE_DISCONNECTED, () => {
        isStethoscopeConnected.value = false;
      });
    };

    const participantsStreams = computed(() =>
      _.filter(
        remoteUserProps,
        (_props) =>
          !useStreams.isClinicianName(_props.name) &&
          _props.name !== DISPLAY_NAME.VUZIX &&
          _props.name !== DISPLAY_NAME.NAVIGATION_USER,
      ),
    );

    const getLabelName = (jitsiId) => {
      const userId = remoteUserProps[jitsiId].userId;
      if (userId === sessionStore.clinicianId.toString()) {
        return sessionStore.sessionDetails.details.clinician.name;
      }
      return remoteUserProps[jitsiId].userId;
    };

    const isMedicalDevice = (jitsiId) =>
      remoteUserProps[jitsiId].userId !== sessionStore.clinicianId.toString();

    const iconSizeCSS = (jitsiId) => {
      if (streamsLayoutStore.focusMode) {
        return jitsiId === streamsLayoutStore.focusedStreamId ? 'h-64 w-64' : 'h-24 w-24';
      }
      return 'h-36 w-36';
    };

    return {
      DISPLAY_NAME,
      iconSizeCSS,
      getLabelName,
      isMedicalDevice,
      setVideoAudioRefs,
      sessionStore,
      ...useStyles,
      ...useStreams,
      remoteUserProps,
      pulseVisualizer,
      clinicianStreams,
      streamsLayoutStore,
      participantsStreams,
      localConnectionQuality,
    };
  },
};
</script>
<style lang="scss" scoped></style>
